import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useAuthStore, useModalStore, useUIStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalHelper } from '@/hooks/useModalHelper';
import { accountApi } from '@/lib/api';
import { trackEvent } from '@/constants/utils/analytics';
import { BORDER_RADIUS, MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isDesktopEditorSidebarType } from '@/constants/utils/files';

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  bubbleContainer: {
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    alignSelf: 'center',
    paddingHorizontal: isXS ? 16 : 0,
  },
  bubbleContainerInner: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 20,
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 10,
  },
  slimContainer: {
    paddingVertical: 8,
    paddingHorizontal: isXS ? 16 : 0,
    alignSelf: 'center',
    width: '100%',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    gap: 10,
  },
  wideContainer: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: isXS ? 16 : 0,
    alignSelf: 'center',
    width:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
        : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
        : '100%',
    maxWidth: '100%',
    gap: 10,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  headerCopy: {
    flex: 1,
    gap: 4,
  },
  dismiss: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: BORDER_RADIUS.round,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    maxWidth: '100%',
    flexShrink: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      },
    }),
  },
  chipHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_input,
  },
  chipLabel: {
    flexShrink: 1,
  },
  choiceActions: {
    gap: 10,
    alignItems: 'flex-start',
    width: '100%',
  },
  choiceButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: isXS ? 'stretch' : 'flex-start',
    gap: 10,
    minHeight: 52,
    minWidth: isXS ? undefined : 320,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: BORDER_RADIUS.round,
    borderWidth: 1,
    maxWidth: '100%',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
      },
    }),
  },
  choiceButtonNeutral: {
    backgroundColor: colors.solid_color_block,
    borderColor: colors.border_color_secondary,
  },
  choiceButtonNeutralHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_input,
  },
  choiceButtonLabel: {
    flexShrink: 1,
    textAlign: 'center',
  },
});

const WorkModeSuggestions = React.memo(({
  prompt,
  fileIds = [],
  onSendToWork,
  onContinueChat,
  disabled = false,
}) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const messageLayout = useUIStore(state => state.messageLayout);
  const messageWidth = useUIStore(state => state.messageWidth);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus === true);
  // Signed-out/ghost users go through onSendToWork so requireAuth prompts
  // sign-in; only a signed-in free user should see the pricing card.
  const needsAuth = useAuthStore(state => !state.user || state.user?.flags?.isGhost === true);
  const workModeSuggestionsEnabled = useAuthStore(
    state => state.user?.preferences?.chat?.workModeSuggestionsEnabled !== false
  );
  const updateUserPreferences = useAuthStore(state => state.updateUserPreferences);
  const { openModal } = useModalStore(state => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  const styles = useMemo(
    () => createStyles(colors, isXS, messageWidth),
    [colors, isXS, messageWidth]
  );

  const handleSend = useCallback(async () => {
    if (disabled || sending) return;
    if (!isPlus && !needsAuth) {
      trackEvent('conversion', 'work_plus_required', 'response_work_mode_suggestion');
      openModal('PricingModal', {
        title: t('work.plusRequired.title'),
        subtitle: t('work.plusRequired.subtitle'),
      });
      return;
    }
    if (!onSendToWork) return;
    trackEvent('work', 'response_work_mode_suggestion_clicked');
    setSending(true);
    try {
      await onSendToWork({ prompt, fileIds });
    } finally {
      setSending(false);
    }
  }, [disabled, sending, isPlus, needsAuth, openModal, onSendToWork, prompt, fileIds, t]);

  const handleHideForever = useCallback(async () => {
    trackEvent('work', 'response_work_mode_suggestion_hidden');
    updateUserPreferences({ chat: { workModeSuggestionsEnabled: false } });
    try {
      await accountApi.updateChatPreferences({ workModeSuggestionsEnabled: false });
    } catch (error) {
      console.error('Error updating work mode suggestions preference:', error);
    }
  }, [updateUserPreferences]);

  const isChoicePrompt = typeof onContinueChat === 'function';

  const handleDismiss = useCallback(() => {
    if (disabled || sending) return;
    trackEvent('work', 'response_work_mode_suggestion_dismissed');
    setDismissed(true);
    if (isChoicePrompt) onContinueChat();
    showConfirmationModal({
      title: t('messages.workModeSuggestion.hideForeverTitle'),
      message: t('messages.workModeSuggestion.hideForeverMessage'),
      confirmText: t('messages.workModeSuggestion.hideForeverConfirm'),
      cancelText: t('messages.workModeSuggestion.hideForeverCancel'),
      onConfirm: handleHideForever,
    });
  }, [disabled, sending, isChoicePrompt, onContinueChat, showConfirmationModal, t, handleHideForever]);

  if (!workModeSuggestionsEnabled) return null;
  if (!isChoicePrompt && isContextSidebarVisible && isDesktopEditorSidebarType(contextSidebarType)) return null;
  if (dismissed) return null;
  if (!prompt?.trim() && !(Array.isArray(fileIds) && fileIds.length)) return null;

  const showPlusCopy = !isPlus && !needsAuth;
  const subtitle = isChoicePrompt ? t('messages.workModeSuggestion.choiceSubtitle') : showPlusCopy
    ? t('messages.workModeSuggestion.plusSubtitle')
    : t('messages.workModeSuggestion.subtitle');
  const sendA11y = isChoicePrompt ? t('messages.workModeSuggestion.send') : showPlusCopy
    ? t('messages.workModeSuggestion.plusSend')
    : t('messages.workModeSuggestion.send');
  const sendLabel = sending ? t('messages.workModeSuggestion.sending') : sendA11y;

  const actions = isChoicePrompt ? (
    <View style={styles.choiceActions}>
      <Pressable
        style={({ hovered, pressed }) => [
          styles.choiceButton,
          styles.choiceButtonNeutral,
          hovered && styles.choiceButtonNeutralHovered,
          (pressed || sending || disabled) && { opacity: 0.8 },
        ]}
        onPress={handleSend}
        disabled={disabled || sending}
        accessibilityRole="button"
        accessibilityLabel={sendA11y}
      >
        <Icon name="briefcase" size={16} color={colors.text_color_primary} />
        <Text size="m" color="primary" weight="semibold" numberOfLines={1} style={styles.choiceButtonLabel}>
          {sendLabel}
        </Text>
        <Icon name="chevron-right" size={14} color={colors.text_color_secondary} />
      </Pressable>
      <Pressable
        style={({ hovered, pressed }) => [
          styles.choiceButton,
          styles.choiceButtonNeutral,
          hovered && styles.choiceButtonNeutralHovered,
          (pressed || disabled || sending) && { opacity: 0.8 },
        ]}
        onPress={onContinueChat}
        disabled={disabled || sending}
        accessibilityRole="button"
        accessibilityLabel={t('messages.workModeSuggestion.continueChat')}
      >
        <Icon name="message" size={16} color={colors.text_color_primary} />
        <Text size="m" color="primary" weight="semibold" numberOfLines={1} style={styles.choiceButtonLabel}>
          {t('messages.workModeSuggestion.continueChat')}
        </Text>
        <Icon name="chevron-right" size={14} color={colors.text_color_secondary} />
      </Pressable>
    </View>
  ) : (
    <Pressable
      style={({ hovered, pressed }) => [
        styles.chip,
        hovered && styles.chipHovered,
        (pressed || sending || disabled) && { opacity: 0.8 },
      ]}
      onPress={handleSend}
      disabled={disabled || sending}
      accessibilityRole="button"
      accessibilityLabel={sendA11y}
    >
      <Icon name="briefcase" size={12} color={colors.text_color_secondary} />
      <Text size="s" color="secondary" weight="semibold" numberOfLines={1} style={styles.chipLabel}>
        {sendLabel}
      </Text>
      <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
    </Pressable>
  );

  const content = (
    <>
      <View style={styles.headerRow}>
        <Icon name="briefcase" size={14} color={colors.text_color_primary} />
        <View style={styles.headerCopy}>
          <Text color="primary" size="m" weight="medium">
            {t('messages.workModeSuggestion.title')}
          </Text>
          <Text color="secondary" size="s">
            {subtitle}
          </Text>
        </View>
        <Pressable
          onPress={handleDismiss}
          accessibilityRole="button"
          accessibilityLabel={t('common.dismiss')}
          hitSlop={8}
          style={({ hovered, pressed }) => [
            styles.dismiss,
            (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
          ]}
        >
          <Icon name="xmark" size={12} color={colors.text_color_tertiary} />
        </Pressable>
      </View>
      {actions}
    </>
  );

  if (messageLayout === MESSAGE_LAYOUT.SLIM) {
    return <View style={styles.slimContainer}>{content}</View>;
  }

  if (messageLayout === MESSAGE_LAYOUT.WIDE) {
    return <View style={styles.wideContainer}>{content}</View>;
  }

  return (
    <View style={styles.bubbleContainer}>
      <View style={styles.bubbleContainerInner}>
        {content}
      </View>
    </View>
  );
});

WorkModeSuggestions.propTypes = {
  prompt: PropTypes.string,
  fileIds: PropTypes.arrayOf(PropTypes.string),
  onSendToWork: PropTypes.func,
  onContinueChat: PropTypes.func,
  disabled: PropTypes.bool,
};

WorkModeSuggestions.displayName = 'WorkModeSuggestions';

export default WorkModeSuggestions;
